<script>
  import { Circle } from "svelte-loading-spinners";
  import { format, isValid, parseISO } from "date-fns";
  import { navigate } from "svelte-routing";
  import { onMount } from "svelte";
  import ArrowLink from "../components/Common/ArrowLink.svelte";
  import FacebookIcon from "../components/Common/Icons/FacebookIcon.svelte";
  import Header from "../components/Common/Header.svelte";
  import MailIcon from "../components/Common/Icons/MailIcon.svelte";
  import TwitterIcon from "../components/Common/Icons/TwitterIcon.svelte";
  import ReportIcon from "../components/Common/Icons/ReportIcon.svelte";
  import findSector from "../helpers/findSector";
  import findSummaryById from "../helpers/findSummaryById";
  import { browser } from "../stores";
  import { isValidURL } from "../helpers/isValidURL";
  import { recallSummaryEntry } from "../helpers/rememberSummaryEntry";
  import Button from "../components/Common/Button.svelte";
  import PdfIcon from "../components/Common/Icons/PdfIcon.svelte";
  import PodcastIcon from "../components/Common/Icons/PodcastIcon.svelte";

  // Props
  export let slug = "";

  // Constants
  const title = WP.summaryIdMap.find((summary) => summary.slug === slug)?.title;
  const buttonStyle =
    "width: 100%; justify-content: center; padding: 16px 18px; box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.1);";
  const socialButtonStyle =
    "justify-content: center; align-items: center; padding: 0; height: 55px; width: 55px; margin-right: 10px;";

  // State
  let recommendations = [];
  let desc = "";
  let summaryContent = "<div></div>";
  let loading = false;

  // Error State
  let contentError = null;

  // Meta Data State
  let type = null;
  let year = null;
  let published = null;
  let sectors = null;
  let reportDownload = null;
  let summaryDownload = null;
  let podcastLink = null;
  let imgSrc = null;
  let relatedReports = null;
  let parentReport = null;
  let childReport = null;

  const report = WP.summaryIdMap.find((summary) => summary.slug === slug);
  const summaryContentDefault = "<p>No content for this summary</p>";

  // Request headers
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  /**
   * Fetches meta data for the Summary
   */
  const fetchMetaData = async () => {
    // API call to get meta data
    const response = await fetch(`/wp-json/reports/report/${report.id}`, {
      method: "GET",
      headers,
    })
      .then((response) => response.json())
      .then((res) => {
        res = JSON.parse(res.body);
        if (Array.isArray(res) && res?.length) {
          const {
            reportType,
            fiscalYear,
            publishDate: rawDate,
            sectorIds,
            url,
            podcastUrl,
            featuredImage,
            relatedReports: rawRelatedReports,
          } = res[0];
          const publishDate =
            typeof rawDate === "string" ? rawDate.split(" ")[0] : null;

          // If report type is valid
          if (
            reportType &&
            reportType.name &&
            typeof reportType.name === "string"
          ) {
            type = reportType.name;
          }
          // If fiscalYear is valid
          if (fiscalYear && Number.isInteger(fiscalYear)) {
            year = fiscalYear;
          }
          // If publishDate is valid
          if (publishDate && isValid(parseISO(publishDate))) {
            published = format(parseISO(publishDate), "MMMM d, yyyy");
          }
          // If result has sectorIds
          if (sectorIds?.length) {
            sectors = sectorIds
              .map(
                (sectorId) =>
                  `<span>${findSector("id", sectorId, "title")}</span>`
              )
              .join("");
          }
          // If download url is valid
          if (!!url && isValidURL(url)) {
            reportDownload = url;
          }
          // If podcast url is valid
          if (!!podcastUrl && isValidURL(url)) {
            podcastLink = podcastUrl;
          }
          // Images
          if (featuredImage?.length) {
            imgSrc = `data:image/jpeg;base64,${featuredImage}`;
          }
          /**
           * Related Reports
           *
           * NOTE: General related reports were axed at the last minute,
           * leaving only "parent" and "child" reports. They are still surfaced
           * on the back end, and this feature may come back some day, so we
           * just filter them out here...
           */
          if (rawRelatedReports.length) {
            relatedReports = rawRelatedReports
              .filter((node) => ["parent", "child"].includes(node.type))
              .map((data) => ({
                ...data,
                summary: findSummaryById(data.summaryId),
              }));
          }
          if (relatedReports.length) {
            parentReport =
              relatedReports.find((report) => report.type === "parent") ?? null;
            childReport =
              relatedReports.find((report) => report.type === "child") ?? null;
          }

          return true;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      })
      .catch(() => {
        return true;
      });

    return response;
  };

  /**
   * Fetches content from WP.summaryId for the Summary
   */
  const fetchContentData = async () => {
    // API Call to get summary HTML, recommendations, and downloads based on summary id (wordpress id)
    const response = await fetch(`/wp-json/single-summary/${report.id}`, {
      method: "GET",
      headers,
    })
      .then((response) => response.json())
      .then((res) => {
        const { content, pdf, description, sidebar_blocks } = res;
        if (content && content.length) {
          summaryContent = content;
          desc = description || "";
        } else {
          summaryContent = summaryContentDefault;
        }
        if (sidebar_blocks && sidebar_blocks.length) {
          recommendations = sidebar_blocks;
        }
        if (pdf && pdf.length) {
          summaryDownload = pdf;
        }
        return true;
      })
      .catch(() => {
        contentError = "Could not load Summary content";
        return true;
      });

    return response;
  };

  /**
   * Fetches meta and content data for Summary depending on if report is found in WP.
   */
  const fetchData = async () => {
    // Set Loading true and show spinner
    loading = true;

    let contentReady = fetchContentData(),
      metaReady = fetchMetaData();

    let response = await Promise.all([contentReady, metaReady]);

    if (response[0] && response[1]) loading = false;
  };

  onMount(() => {
    // fetch meta and content data based on if report found in WP.summaryIdMap
    fetchData();
    if (browser.name === "ie") {
      window.scrollTo(0, 0);
    }
  });

  /**
   * Opens share link of this current URL to Facebook, Twitter, or Email
   * @param network string
   */
  const shareToSocial = (network) => {
    switch (network) {
      case "Facebook":
        const opts = `status=1,width=${575},height=${400},top=${
          window.innerWidth / 2
        },left=${window.innerHeight / 2}`;
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
          "facebook-share-dialog",
          opts
        );
        break;
      case "Twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${window.location.href}`
        );
        break;
      case "Mail":
        window.open(
          `mailto:?subject=DOAA Report Summary${
            report?.title ? `: ${report.title}` : ""
          }&body=${window.location.href}`
        );
        break;
      default:
        break;
    }
  };

  /**
   * Handle back button
   */
  const summaryEntry = recallSummaryEntry();
  let backText = "Reports";
  if (summaryEntry && summaryEntry.pathname.includes("search"))
    backText = "Back to Report Results";
  const handleBackButton = () => {
    if (backText === "Reports") {
      if (browser.name === "ie") {
        window.location = "/reports";
      } else {
        navigate("/reports");
      }
    } else {
      window.location = summaryEntry.href;
    }
  };
</script>

{#if loading}
  <div class="loading-page">
    <Circle size="100" color="#4a4a4a" unit="px" />
  </div>
{:else}
  <Header>
    {#if imgSrc}
      <div class="report-image__wrap">
        <figure class="report-image__overlay" />
        <img src={imgSrc} alt={title} />
      </div>
    {/if}
    <div class="split-header">
      <div class="summary-meta" class:no-img={!imgSrc}>
        <ArrowLink
          button
          buttonSubmit={handleBackButton}
          text={backText}
          reverse
        />
        <h1 style="margin-top: 14px">{title}</h1>
        {#if desc}
          <p class="report-description">
            {@html desc}
          </p>
        {/if}
        <div class="report-meta">
          {#if type}
            <span class="report-meta__heading">Type:</span>
            <span class="report-meta__value">{type}</span>
          {/if}
          {#if published}
            <span class="report-meta__heading">Published:</span>
            <span class="report-meta__value">{published}</span>
          {/if}
          {#if sectors}
            <span class="report-meta__heading">Sectors:</span>
            <span class="report-meta__value multiline">{@html sectors}</span>
          {/if}
          {#if year}
            <span class="report-meta__heading">Fiscal Year:</span>
            <span class="report-meta__value">{year}</span>
          {/if}
          {#if parentReport}
            <span class="report-meta__heading">Follows:</span>
            <span class="report-meta__value">
              {#if parentReport.summary?.slug}
                <a href={`/reports/summaries/${parentReport.summary.slug}`}>
                  {parentReport.title}
                </a>
              {:else}
                <a href={parentReport.url} target="_blank">
                  {parentReport.title}
                </a>
              {/if}
            </span>
          {/if}
          {#if childReport}
            <span class="report-meta__heading">Followed by:</span>
            <span class="report-meta__value">
              {#if childReport.summary?.slug}
                <a href={`/reports/summaries/${childReport.summary.slug}`}>
                  {childReport.title}
                </a>
              {:else}
                <a href={childReport.url} target="_blank">
                  {childReport.title}
                </a>
              {/if}
            </span>
          {/if}
        </div>
      </div>
      <div class="header-downloads">
        {#if reportDownload}
          <Button
            class="orange"
            style={buttonStyle}
            href={reportDownload}
            target="_blank"
          >
            <PdfIcon />
            Get the Full Report
          </Button>
        {/if}
        {#if summaryDownload}
          <Button style={buttonStyle} href={summaryDownload} target="_blank">
            <PdfIcon />
            Report Summary
          </Button>
        {/if}
        {#if podcastLink}
          <Button style={buttonStyle} href={podcastLink} target="_blank">
            <PodcastIcon />
            Video Summary
          </Button>
        {/if}
      </div>
    </div>
  </Header>

  <div class="container">
    <div class="wysiwyg summary-content">
      {#if contentError}
        <p style="color: red;">
          {@html contentError}
        </p>
      {:else}
        {@html summaryContent}
      {/if}
      <div style="height: 60px;" />
      <ArrowLink
        button
        buttonSubmit={() => window.history.back()}
        text="Back to Report Results"
        reverse
      />
    </div>
    <div class="summary-sidebar">
      <div class="wysiwyg">
        {#each recommendations as recommendation}
          <div class="recommendation-block">
            <h3>{recommendation.heading}</h3>
            {@html recommendation.text}
          </div>
        {/each}
      </div>
      {#if relatedReports?.length}
        <div class="related-reports">
          <h3>Related Report{relatedReports.length > 1 ? "s" : ""}</h3>
          {#each relatedReports as { title, url, summary, type, publishDate }}
            <div class="related-report">
              <ReportIcon />
              <div class="related-report__text">
                {#if summary?.slug}
                  <a href={`/reports/summaries/${summary.slug}`}>{title}</a>
                {:else}
                  <a href={url} target="_blank">{title}</a>
                {/if}

                {#if type === "parent"}
                  <span class="related-report__relationship">
                    Original Report
                  </span>
                {/if}
                {#if type === "child"}
                  <span class="related-report__relationship">
                    Follow-up Report
                  </span>
                {/if}

                {#if publishDate && isValid(parseISO(publishDate))}
                  <span class="related-report__date">
                    {format(parseISO(publishDate), "MMMM d, yyyy")}
                  </span>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {/if}
      <div class="summary-view-social-share">
        <h3>Share</h3>
        <div>
          <Button
            style={socialButtonStyle}
            onClick={() => shareToSocial("Facebook")}
          >
            <FacebookIcon />
          </Button>
          <Button
            style={socialButtonStyle}
            onClick={() => shareToSocial("Twitter")}
          >
            <TwitterIcon />
          </Button>
          <Button
            style={socialButtonStyle}
            onClick={() => shareToSocial("Mail")}
          >
            <MailIcon />
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  :global(.single-summary .reports-header) {
    padding-bottom: 0;
  }

  .report-description {
    color: #4a4a4a;
    @media (max-width: 850px) {
      color: white;
    }
  }

  .report-image {
    &__wrap {
      position: absolute;
      height: calc(100% + 80px);
      right: calc(-1 * (100vw - 100%) / 2);
      top: -80px;
      z-index: -1;
      width: 30vw;
      min-width: 400px;
      max-width: 580px;

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }

      @media (min-width: 1680px) {
        & {
          right: -138px;
        }
      }
      @media (max-width: 1020px) {
        display: none;
      }
    }

    &__overlay {
      position: absolute;
      margin: 0;
      top: 0;
      right: 0;
      height: 100%;
      width: 100%;
      background: rgb(255, 255, 255);
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 1) 10%,
        rgba(255, 255, 255, 0.4) 90%
      );
    }
  }
  .split-header {
    display: flex;
    align-items: flex-end;
  }

  .summary-meta {
    width: calc(69.03% /*100%-30.97%*/ - 44px);
    padding-right: 44px;
    position: relative;

    &.no-img:after {
      content: "";
      display: block;
      position: absolute;
      background-color: #f6f6f6;
      height: calc(100% + 80px);
      width: 100vw;
      left: 100%;
      bottom: 0;
      z-index: -1;
    }
  }
  .header-downloads {
    padding: 44px 0;
    margin-left: 44px;
    width: 30.97%; //420/1356
    max-width: 373px;

    display: flex;
    flex-direction: column;
    gap: 15px;
    position: relative;
  }

  .report-meta {
    margin-top: 40px;
    padding: 24px 0;
    border-top: 1px solid #ebe9e9;
    border-bottom: 1px solid #ebe9e9;

    display: grid;
    grid-template-columns: 100px 1fr 100px 1fr;
    row-gap: 15px;
    column-gap: 10px;

    &__heading {
      font-family: "Georgia", serif;
      font-size: 16px;
      font-weight: 400;
      color: #9b9b9b;
      text-align: right;
    }

    &__value {
      font-size: 16px;
      line-height: 24px;
      color: #4a4a4a;

      &.multiline {
        display: inline-flex;
        flex-direction: column;
        gap: 4px;
      }
    }

    @media (max-width: 620px) {
      grid-template-columns: 100px 1fr;
    }
  }

  .container {
    display: flex;
    justify-content: space-between;
    padding: 80px 30px;
  }

  .summary-content {
    width: 69.03%; // 100% - 30.97%
    max-width: 674px;
  }

  .summary-sidebar {
    width: 30.97%; //420/1356
  }

  .summary-sidebar :global(h3) {
    color: #4a4a4a;
    font-family: "Segoe UI", sans-serif;
    font-size: 2.4rem;
    line-height: 3rem;
    font-weight: 500; // designs call for 600 but 500 looks better due to Segoe UI vs Avenir
  }

  .summary-view-social-share div {
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    margin-top: 30px;
  }

  .loading-page {
    height: 80vh;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .recommendation-block {
    padding-bottom: 30px;
  }

  .recommendation-block,
  .related-reports {
    border-bottom: 1px solid #ebe9e9;
    margin-bottom: 40px;
  }

  .related-reports {
    h3 {
      margin-bottom: 30px;
    }
  }
  .related-report {
    margin-bottom: 30px;
    display: flex;
    align-items: flex-start;
    gap: 10px;

    :global(svg) {
      flex-shrink: 0;
      color: #006b97;
      position: relative;
      top: 3px;
    }

    &__text {
      display: flex;
      flex-direction: column;
    }

    a {
      font-size: 18px;
      line-height: 24px;
      font-weight: 500; // designs call for 600 but 500 looks better due to Segoe UI vs Avenir
      letter-spacing: 0.2px;
      margin-bottom: 5px;
    }

    &__relationship {
      color: #d07743;
      font-size: 13px;
      line-height: 24px;
      font-weight: 600; // designs call for 700 but 600 looks better due to Segoe UI vs Avenir
      letter-spacing: 0.2px;
      text-transform: uppercase;
    }

    &__date {
      color: #636768;
      font-size: 13px;
      line-height: 24px;
      letter-spacing: 0.2px;
    }
  }
  @media (max-width: 1120px) {
    .container {
      flex-direction: column;
      padding: 60px 30px 0;
    }
    .summary-content {
      width: 100%;
    }
    .summary-sidebar {
      background: #f6f6f6;
      max-width: none;
      width: 100vw;
      padding: 0 30px;
    }
    .summary-sidebar {
      position: relative;
      left: -30px;
      padding-top: 40px;
      padding-bottom: 60px;
    }
    .summary-sidebar > * {
      max-width: 674px;
      margin: 0 auto 30px;
    }
    .summary-content {
      padding-right: 0;
      margin: 0 auto 60px;
    }
  }
  @media (max-width: 1020px) {
    .split-header {
      flex-direction: column;
      align-items: flex-start;
    }
    .summary-meta {
      width: 100%;
      padding-right: 0;

      &:after {
        display: none;
      }
    }
    .report-meta {
      border-bottom-width: 0;
    }
    .header-downloads {
      width: 100vw;
      position: relative;
      left: -30px;
      max-width: none;
      margin: 0;
      padding: 30px;
      background: #f6f6f6;

      flex-direction: row;
      justify-content: space-evenly;

      :global(.report-button) {
        width: auto !important;
      }
    }
  }

  @media (max-width: 850px) {
    .report-meta {
      border-color: #636768;
      border-bottom-width: 1px;
    }
    .report-meta__value {
      color: white;
    }
    .header-downloads {
      background-color: transparent;
      width: 100%;
      position: static;
      padding: 30px 0;
    }
  }
  @media screen and (max-width: 768px) {
    .header-downloads {
      flex-direction: column;
      margin: 0 auto;
      align-items: center;

      :global(.report-button) {
        width: 100% !important;
        max-width: 373px !important;
      }
    }
  }
</style>
